import type {
  DateRange,
  DateRange as MuiDateRange,
} from "@mui/x-date-pickers-pro";
import groupBy from "lodash/groupBy.js";
import { useMemo } from "react";
import { useConstructionSites } from "~api/constructionSites.ts";
import { useAllEmployees } from "~api/employees.ts";
import { useWorkPackages } from "~api/workPackages.ts";
import {
  useOverlappingTimeEntryIds,
  useTimeEntries,
} from "~components/TimeManagement/api/timeEntries.ts";
import { useResources } from "~contexts/ResourceContext/ResourceContext.tsx";
import type { TimeEntry } from "~generated";
import { Resource as APIResource } from "~generated";
import { DataProvider } from "./DataProvider.ts";
import { EmbeddedTimeEntry } from "./EmbeddedTimeEntry.ts";
import { getAnomalies } from "./getAnomalies.ts";
import {
  timeEntriesGroupedByDateAndResourceKey,
  timeEntriesGroupedByDateAndTimeTrackingKey,
} from "./groupTimeEntries.ts";

interface UseEmbeddedTimeEntriesForDateRangeProps {
  dateRange?: DateRange<Date>;
  enabled?: boolean;
}

export function useEmbeddedTimeEntriesForDateRange({
  dateRange,
  enabled = true,
}: UseEmbeddedTimeEntriesForDateRangeProps = {}) {
  const {
    data: timeEntries,
    isLoading: isLoadingTimeEntries,
    isFetching: isFetchingTimeEntries,
  } = useTimeEntries(dateRange, { enabled });

  const {
    embeddedTimeEntries,
    isLoading: isLoadingEmbeddedTimeEntries,
    isFetching: isFetchingEmbeddedTimeEntries,
  } = useEmbeddedTimeEntries({
    timeEntries: timeEntries || [],
    dateRange,
    enabled,
  });

  const isLoading = isLoadingTimeEntries || isLoadingEmbeddedTimeEntries;
  const isFetching = isFetchingTimeEntries || isFetchingEmbeddedTimeEntries;

  return {
    embeddedTimeEntries,
    isLoading,
    isFetching,
  };
}

type UseEmbeddedTimeEntriesProps = {
  timeEntries: TimeEntry[];
  dateRange: MuiDateRange<Date> | undefined;
  enabled?: boolean;
};
export function useEmbeddedTimeEntries({
  timeEntries,
  dateRange,
  enabled = true,
}: UseEmbeddedTimeEntriesProps) {
  const {
    data: employees,
    isLoading: isLoadingEmployees,
    isFetching: isFetchingEmployees,
  } = useAllEmployees(undefined, { enabled });

  const {
    allResources,
    isLoading: isLoadingResources,
    isFetching: isFetchingResources,
  } = useResources();

  const {
    data: constructionSites,
    isLoading: isLoadingConstructionSites,
    isFetching: isFetchingConstructionSites,
  } = useConstructionSites({
    includeDeleted: true,
    useQueryOptions: { enabled },
  });

  const {
    data: workPackages,
    isLoading: isLoadingWorkPackages,
    isFetching: isFetchingWorkPackages,
  } = useWorkPackages({}, { enabled });

  const {
    data: overlaps,
    isLoading: isLoadingOverlaps,
    isFetching: isFetchingOverlaps,
  } = useOverlappingTimeEntryIds(dateRange, { enabled });

  const isLoading =
    isLoadingEmployees ||
    isLoadingResources ||
    isLoadingConstructionSites ||
    isLoadingWorkPackages ||
    isLoadingOverlaps;

  const isFetching =
    isFetchingEmployees ||
    isFetchingResources ||
    isFetchingConstructionSites ||
    isFetchingWorkPackages ||
    isFetchingOverlaps;

  const embeddedTimeEntries: EmbeddedTimeEntry[] = useMemo(() => {
    if (isLoading || !timeEntries) {
      return [];
    }

    const dataProvider = new DataProvider(
      constructionSites,
      allResources,
      workPackages || [],
      employees || [],
      overlaps || [],
    );

    const result = timeEntries.map<EmbeddedTimeEntry>(
      (timeEntry) => new EmbeddedTimeEntry(timeEntry, dataProvider),
    );

    // This is a performance optimization to avoid iterating over all time entries
    // in the isExceedingMaxHours function.
    const timeEntriesGroupedByDateAndEmployee = groupBy(
      result,
      timeEntriesGroupedByDateAndResourceKey,
    );

    const timeEntriesGroupedByDateAndTimeTracking = groupBy(
      result,
      timeEntriesGroupedByDateAndTimeTrackingKey,
    );

    const filtered = result.filter(
      (timeEntry) =>
        timeEntry.stopTime &&
        !timeEntry.deletedAt &&
        timeEntry.resource.type === APIResource.type.EMPLOYEE,
    );
    for (const timeEntry of filtered) {
      timeEntry.anomalies = getAnomalies({
        timeEntry,
        timeEntriesGroupedByDateAndEmployee,
        timeEntriesGroupedByDateAndTimeTracking,
      });
    }

    return result;
  }, [
    isLoading,
    timeEntries,
    employees,
    workPackages,
    constructionSites,
    overlaps,
    allResources,
  ]);

  return {
    embeddedTimeEntries,
    isLoading,
    isFetching,
  };
}
